import { Injectable, inject } from '@angular/core';
import { ConfigurationService } from '../configuration/configuration.service';
import { DevAuthService } from '../dev-service/dev-auth/dev-auth.service';
import { LoggerService } from '../logger-services/logger.service';
import { AgentFeatureService } from '../../app/agents/services/agent-feature/agent-feature.service';
import { AgentConfigService } from '../../app/agents/services/agent-config/agent-config.service';
import Config from '../../config/config.json'

@Injectable({
  providedIn: 'root',
})
export class AppInitializerService {
  private readonly configService = inject(ConfigurationService);
  private readonly devAuthService = inject(DevAuthService);
  private readonly loggerService = inject(LoggerService);
  private readonly agentFeatureService = inject(AgentFeatureService);
  private readonly agentConfigService = inject(AgentConfigService);

  private readonly AppInitializerServiceError = (error:any) =>
    `[AppInitializerService] - ${JSON.stringify(error)}`

  /**
   * Initialize the application by loading configuration, handling dev auth, and loading feature flags
   */
  async initializeApp(): Promise<void> {
    try {
      // Load basic configuration first
      await this.configService.loadConfiguration();
      const config = this.configService.config;
      
      // Handle dev authentication ONLY if ENVIRONMENT is set to 'dev' or 'development'
      if (config?.ENVIRONMENT === 'development' || config?.ENVIRONMENT === 'dev') {
        try {
          Config.LoginForm = true;
          // Sync overrides / userData when there is no full session yet, or when the session is valid.
          // Skip when cookies look logged-in but the JWT access token is expired (user should log in again).
          if (
            this.devAuthService.hasActiveDevSession() ||
            !this.devAuthService.isAlreadyAuthenticated()
          ) {
            this.devAuthService.updateEnvironmentWithCookies();
          }

        } catch (authError) {
          // Don't throw error, just continue - app should still load
        }
      }


      // ✅ Check if agents feature is available (safe - won't break if folder missing)
      try {
        console.log('[AppInitializerService] Checking agent feature availability...');
        const agentsAvailable = await this.agentFeatureService.initialize();
        console.log('[AppInitializerService] Agents available:', agentsAvailable);
        
        // Only initialize agent system if agents are available
        if (agentsAvailable) {
          try{
            console.log('[AppInitializerService] Initializing agent access control...');
            
            // Pre-load agent configurations
            console.log('[AppInitializerService] Loading agent configurations...');
            await this.agentConfigService.loadAgentConfig();
            console.log('[AppInitializerService] ✅ Agent configurations loaded successfully');
          } catch (error) {
            console.error('[AppInitializerService] Error initializing agent access control:', error);
            this.loggerService.logError(this.AppInitializerServiceError(error),"initializeApp");
          }
        }
      } catch (error) {
        console.log('[AppInitializerService] Agents feature not available, continuing without agents');
        this.loggerService.logInfo(this.AppInitializerServiceError(error),"initializeApp");
      }
      
      // Try to load feature flags (this might fail due to auth, but that's okay)
      try {
        await this.configService.loadFeatureFlags();
      } catch (error) {
        // Feature flags loading can fail due to auth issues, continue with app initialization
        this.loggerService.logError(this.AppInitializerServiceError(error),"initializeApp");
      }
      // Set release version for i18n (V2 vs V3) before TranslateService loads. Do NOT call getAvailableAgents() here — it hits the network
      // and can hang or fail DI during bootstrap → blank page.
      try {
        await this.persistI18nReleaseVersion();
      } catch {
        this.configService.setReleaseVersion('2.0');
      }
    } catch (error) {
      this.loggerService.logError(this.AppInitializerServiceError(error),"initializeApp");
      throw error;
    }
  }
  
  /**
   * Default i18n bundle to V2 at bootstrap. ChatbotWrapper sets 3.0 only when V3 UI
   * is confirmed (ACL ∩ enrolled agents) and reloads translations via applyLanguage().
   * Do not set 3.0 here from tenant-level flags alone — that caused V2 UI to render
   * with V3 translation keys (missing keys shown as raw strings).
   */
  private async persistI18nReleaseVersion(): Promise<void> {
    this.configService.setReleaseVersion('2.0');
  }
}

/**
 * Factory function for APP_INITIALIZER
 */
export function initializeApp(appInitializer: AppInitializerService) {
  return () => appInitializer.initializeApp();
}
